import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, field } from "@/components/nds/windows/finance/parts";

export const SESSION_TYPES = [["case_management", "Case management"], ["life_skills", "Life skills"], ["employment", "Employment"], ["other", "Other"]];
const today = () => new Date().toISOString().slice(0, 10);

export default function PayoutForm({ onDone, onCancel }) {
  const [staff, setStaff] = useState([]);
  const [residents, setResidents] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ staff_id: "", session_date: today(), session_type: "case_management", hours: "", rate_per_hour: "", notes: "", resident_ids: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    finApi.get("/session-payouts/staff").then((d) => setStaff(d.staff)).catch(() => {});
    finApi.get("/residents", { status: "active" }).then((d) => setResidents(d.residents)).catch(() => {});
  }, []);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const toggle = (id) => setF((x) => ({ ...x, resident_ids: x.resident_ids.includes(id) ? x.resident_ids.filter((r) => r !== id) : [...x.resident_ids, id] }));
  const gross = (Number(f.hours) || 0) * (Number(f.rate_per_hour) || 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await finApi.post("/session-payouts", { ...f, hours: Number(f.hours), rate_per_hour: Number(f.rate_per_hour) }); toast.success("Draft payout created"); onDone(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  const shown = residents.filter((r) => `${r.first_name} ${r.last_name}`.toLowerCase().includes(q.toLowerCase())).slice(0, 40);
  return (
    <form onSubmit={submit} data-testid="fin-payout-form" className="p-4 border border-nex-gold/30 rounded-sm space-y-3 bg-white/[0.02]">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <select required value={f.staff_id} onChange={set("staff_id")} data-testid="fin-payout-form-staff" className={`${field} md:col-span-2`}>
          <option value="">Staff member…</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
        </select>
        <input type="date" required value={f.session_date} onChange={set("session_date")} data-testid="fin-payout-form-date" className={field} />
        <select value={f.session_type} onChange={set("session_type")} data-testid="fin-payout-form-type" className={field}>
          {SESSION_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" required min="0.25" step="0.25" value={f.hours} onChange={set("hours")} placeholder="Hours" data-testid="fin-payout-form-hours" className={field} />
          <input type="number" required min="0" step="0.01" value={f.rate_per_hour} onChange={set("rate_per_hour")} placeholder="$/hr" data-testid="fin-payout-form-rate" className={field} />
        </div>
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Residents in session · ${f.resident_ids.length} selected — search…`} data-testid="fin-payout-form-res-search" className={`${field} w-full !py-1.5`} />
      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
        {shown.map((r) => {
          const on = f.resident_ids.includes(r.id);
          return (
            <button key={r.id} type="button" onClick={() => toggle(r.id)} data-testid={`fin-payout-form-res-${r.id}`} aria-pressed={on}
              className={`px-2 py-0.5 rounded-full border text-[11px] transition-colors ${on ? "border-nex-gold bg-nex-gold/15 text-nex-gold" : "border-white/10 text-nex-muted hover:text-nex-silver"}`}>
              {r.first_name} {r.last_name}
            </button>
          );
        })}
      </div>
      <input value={f.notes} onChange={set("notes")} placeholder="Notes (optional)" data-testid="fin-payout-form-notes" className={`${field} w-full`} />
      <div className="flex items-center gap-2">
        <span data-testid="fin-payout-form-gross" className="font-mono text-sm text-nex-gold">Gross {money(gross)}</span>
        <button type="button" onClick={onCancel} data-testid="fin-payout-form-cancel" className="ml-auto font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Cancel</button>
        <button type="submit" disabled={busy || !f.staff_id || !gross} data-testid="fin-payout-form-submit" className={btnGold}>Submit draft</button>
      </div>
    </form>
  );
}
